/// <reference types="vite/client" />
import { PublicClientApplication, Configuration } from '@azure/msal-browser';

/**
 * Production is temporarily served on two domains (myapps.br.sodexonet.com and
 * myapps.sodexonet.com) while the new domain rolls out, but Vite inlines VITE_ env vars at
 * build time — a single prd build can't hardcode two different redirect URIs. When the
 * current origin is in VITE_ENTRA_REDIRECT_ORIGINS, redirect back to THAT origin instead of
 * the static one, so MSAL's per-origin sessionStorage PKCE state actually round-trips.
 * Unset (dev/uat/sbx/test) or an origin not on the list both fall back to the static env
 * vars unchanged — Azure AD independently rejects any redirect URI it doesn't recognize
 * anyway, so an unlisted origin fails the same way it always has.
 */
const getAllowedRedirectOrigins = (): string[] =>
  (import.meta.env.VITE_ENTRA_REDIRECT_ORIGINS as string | undefined)
    ?.split(',')
    .map((origin) => origin.trim())
    .filter((origin) => origin.length > 0) ?? [];

const isCurrentOriginAllowed = (): boolean =>
  typeof window !== 'undefined' && getAllowedRedirectOrigins().includes(window.location.origin);

export const getRedirectUri = (): string =>
  isCurrentOriginAllowed()
    ? `${window.location.origin}/login`
    : (import.meta.env.VITE_ENTRA_REDIRECT_URI as string);

export const getPostLogoutRedirectUri = (): string =>
  isCurrentOriginAllowed()
    ? window.location.origin
    : (import.meta.env.VITE_ENTRA_POST_LOGOUT_REDIRECT_URI as string);

export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID as string,
    authority: import.meta.env.VITE_ENTRA_AUTHORITY as string,
    redirectUri: getRedirectUri(),
    postLogoutRedirectUri: getPostLogoutRedirectUri(),
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
  },
};

export const loginRequest = {
  scopes: [
    'openid',
    'profile',
    'email',
    `api://${import.meta.env.VITE_ENTRA_CLIENT_ID}/access_as_user`,
  ],
};

// Create instance but don't initialize synchronously
export const msalInstance = new PublicClientApplication(msalConfig);

export const initializeMSAL = async (): Promise<void> => {
  try {
    await msalInstance.initialize();
    // Complete any interactive-login redirect flow. MSAL needs this to process
    // the returned auth code; OSAT no longer keys off the result (the survey
    // trigger fires on first settled eligibility, any auth path — 859323).
    await msalInstance.handleRedirectPromise();
  } catch (error) {
    console.error('MSAL initialization error:', error);
    throw error;
  }
};

/**
 * Attempts SSO silent authentication.
 * This will work when user is logged into Windows and uses a compatible browser.
 * @returns Promise that resolves to AuthenticationResult if successful, null otherwise
 */
export const attemptSsoSilent = async () => {
  try {
    const response = await msalInstance.ssoSilent({
      scopes: loginRequest.scopes,
    });
    return response;
  } catch (error) {
    // SSO silent failed - user is not logged in via Windows or browser doesn't support it
    console.log('SSO Silent authentication not available:', error);
    return null;
  }
};
